import { Constant } from '@genial/config';
import { Utils } from '@genial/utils';
import { jwtDecode } from 'jwt-decode';

import { commissionsPermissionQuery, insurancePermissionQuery } from '../permission/queries';

import { ApplicationSession, UserProfile, UserProfileLegacy } from './types';

export const buildSessionAtomFromTokens = (
  jwtToken: string,
  refreshToken: string,
  expirationRefreshToken: string,
  userProfile: UserProfileLegacy
): ApplicationSession => {
  const decoded = jwtDecode(jwtToken);
  const initials = userProfile?.nomeRecurso
    ?.split(' ')
    ?.map((name) => name[0])
    ?.splice(0, 2)
    ?.join('');

  const combinedUserProfile: UserProfile = {
    ...userProfile,
    initials,
  };

  return {
    signedIn: true,
    token: jwtToken || '',
    refreshToken: refreshToken || '',
    refreshTokenExpiresAt: new Date(expirationRefreshToken),
    expiresAt: new Date((decoded.exp as number) * 1000),
    userProfile: combinedUserProfile,
    isMaster: userProfile.masterGrupo || userProfile.masterParceiro,
  };
};

export const buildSessionAtomFromLocalStorage = async () => {
  const jwtToken = await Utils.Storage.GetItem(Constant.TokenKey);
  const refreshToken = await Utils.Storage.GetItem(Constant.RefreshTokenKey);
  const expirationRefreshToken = await Utils.Storage.GetItem(Constant.RefreshTokenKeyExpiration);
  const userProfile = await Utils.Storage.GetItem(Constant.UserProfileKey);

  return buildSessionAtomFromTokens(
    jwtToken as string,
    refreshToken as string,
    expirationRefreshToken as string,
    JSON.parse(userProfile as string)
  );
};

export const sessionExpirationFromLocalStorage = async () => {
  const jwtToken = await Utils.Storage.GetItem(Constant.TokenKey);
  const decoded = jwtDecode(jwtToken as string);
  return new Date((decoded.exp as number) * 1000);
};

export const refreshTokenFromLocalStorage = async () => {
  const refreshToken = await Utils.Storage.GetItem(Constant.RefreshTokenKey);
  return refreshToken as string;
};

export const updateSessionWithSignedInJsonToken = async (
  previousSession: ApplicationSession,
  setSession: (session: ApplicationSession) => void,
  jsonTokenObject: any
) => {
  await Utils.Storage.SetItem(Constant.TokenKey, jsonTokenObject.jwtToken);
  await Utils.Storage.SetItem(Constant.RefreshTokenKey, jsonTokenObject.refreshToken);
  await Utils.Storage.SetItem(
    Constant.RefreshTokenKeyExpiration,
    jsonTokenObject.expirationRefreshToken
  );

  const sessionData = buildSessionAtomFromTokens(
    jsonTokenObject.jwtToken,
    jsonTokenObject.refreshToken,
    jsonTokenObject.expirationRefreshToken,
    previousSession.userProfile as UserProfileLegacy
  );

  const commissionsPermission = await commissionsPermissionQuery(jsonTokenObject.jwtToken);
  const insurancePermission = await insurancePermissionQuery(jsonTokenObject.jwtToken);
  const mergedSession = {
    ...sessionData,
    userProfile: sessionData.userProfile
      ? {
          ...sessionData.userProfile,
          exibirComissoes: commissionsPermission && sessionData.userProfile.masterParceiro,
          ExibirSeguros: insurancePermission,
        }
      : undefined,
  };

  await Utils.Storage.SetItem(Constant.UserProfileKey, JSON.stringify(mergedSession.userProfile));

  setSession(mergedSession);
};
